/**
 * The application (E9-01 … E9-11).
 *
 * Three screens behind a hash router: ask, base, changes. State is a module-level
 * object and rendering is "throw the subtree away and rebuild it" — at a hundred rows a
 * page that is imperceptible, and it removes every class of bug where the DOM and the
 * data disagree.
 *
 * The one thing this file is careful about throughout: **a value and what we know about
 * it are never separated.** Every cell knows its source, its age and whether it was
 * ever checked, because a table of unattributed facts is precisely the product this is
 * not.
 */

import { api, ApiError } from './api.ts';
import type { EnrichmentProgress, PlanInfo } from './api.ts';
import type { BaseSummary, CellValue, CustomColumn, FieldSpec, Plan, Preview, Row, SessionInfo } from './api.ts';
import { age, display, el, fill, frag, intervalMs, money, plural, stamp } from './dom.ts';

/**
 * The password floor, repeated rather than imported.
 *
 * The client is its own TypeScript project with no server packages in scope, and pulling
 * `@databasis/accounts` in for one number would drag the database layer into a browser
 * bundle. The server decides; this copy exists only so the form can say no before a round
 * trip, and `apps/app/test/shell.test.ts` fails if the two numbers stop matching.
 */
const PASSWORD_MIN = 12;

interface State {
  /** Null until the session has been checked; the app renders nothing conclusive before. */
  session: SessionInfo | null;
  route:
    | { name: 'ask' }
    | { name: 'base'; id: number }
    | { name: 'changes'; id: number }
    | { name: 'billing'; returned: 'success' | 'cancelled' | null }
    | { name: 'account' }
    | { name: 'signup' };
  fields: Map<string, FieldSpec>;
  bases: BaseSummary[];
  /**
   * What is typed in the ask box.
   *
   * Held in state, not only in the DOM: any re-render rebuilds the textarea, so a
   * failed request would otherwise discard whatever the user had written — at exactly
   * the moment they need it most.
   */
  asked: string;
  /** The plan being composed on the ask screen, once there is one. */
  draft: { plan: Plan; request: string; preview: Preview | null } | null;
  busy: string | null;
  error: ApiError | null;
  /**
   * One line of "that worked", cleared by the next navigation.
   *
   * Errors have `banner()`; a success had nothing, so changing a password looked exactly
   * like not changing it. It lives in state rather than in the screen because the screen
   * re-renders to show the new state, which is the moment the message has to survive.
   */
  notice: string | null;
}

const state: State = {
  session: null,
  route: { name: 'ask' },
  fields: new Map(),
  bases: [],
  asked: '',
  draft: null,
  busy: null,
  error: null,
  notice: null,
};

const root = document.getElementById('app')!;

/* ------------------------------------------------------------------ chrome -- */

/** The bar mark, geometry from the vector source (see packages/brand). */
function mark(size = 20): SVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 86 70.5');
  svg.setAttribute('height', String(size));
  svg.setAttribute('width', String(Math.round(size * 1.224)));
  svg.setAttribute('aria-hidden', 'true');
  for (let row = 0; row < 4; row++) {
    for (const col of [0, 46.3]) {
      const bar = document.createElementNS(ns, 'rect');
      bar.setAttribute('x', String(col));
      bar.setAttribute('y', String(row * 19.185));
      bar.setAttribute('width', '39.664');
      bar.setAttribute('height', '12.665');
      bar.setAttribute('rx', '1.2');
      // Teal is a fill and only a fill; the rest is azure chrome.
      bar.setAttribute('fill', row === 3 && col !== 0 ? '#00D2C3' : '#3E88D4');
      svg.appendChild(bar);
    }
  }
  return svg;
}

/**
 * The trial, said out loud.
 *
 * A trial nobody can see the end of ends as a surprise, and the product had no way to
 * mention it: the session endpoint did not send the date or the balance, so no screen
 * could. This is one strip across the top of every signed-in screen rather than a modal,
 * because the thing it interrupts is the work the trial exists to demonstrate.
 *
 * It changes at the two moments that matter, which are moments in the *product* and not
 * on the calendar: when the credit runs out, and when the days do. Asking for a card at
 * either is asking when the trial has just proved its point; asking on day one is asking
 * a stranger.
 */
function trialBar(): HTMLElement | null {
  const trial = state.session?.trial;
  if (trial === undefined || trial === null) {
    // A trial that ran out was cancelled by housekeeping (D32) and `trialState` stops
    // reporting it, which left the strip vanishing at the exact moment it had something
    // to say. Reading still works and spending has stopped (D21); saying neither is how
    // a product comes to look broken instead of unpaid.
    if (state.session?.status !== 'canceled') return null;
    return el(
      'div',
      { class: 'trial-bar trial-bar-urgent', role: 'status' },
      el('strong', { text: 'Your trial has ended' }),
      el('span', {
        class: 'detail',
        text: 'Your data and your bases are still here. Choose a plan to fill columns again.',
      }),
      el('span', { class: 'spacer' }),
      el('button', { class: 'btn btn-small btn-primary', text: 'Choose a plan', onclick: () => go('#/billing') }),
    );
  }

  const spent = trial.creditRemaining <= 0;
  const soon = trial.daysLeft <= 3;
  const remaining =
    trial.daysLeft === 0 ? 'ends today' : `has ${plural(trial.daysLeft, 'day', 'days')} left`;

  return el(
    'div',
    { class: `trial-bar${spent || soon ? ' trial-bar-urgent' : ''}`, role: 'status' },
    el('strong', {
      text: spent
        ? 'Your trial credit is spent'
        : trial.daysLeft === 0
          ? 'Your trial ends today'
          : `${plural(trial.daysLeft, 'day', 'days')} left in your trial`,
    }),
    el('span', {
      class: 'detail',
      text: spent
        ? `The trial ${remaining}. Choose a plan to keep filling columns.`
        : `${money(trial.creditRemaining)} of ${money(trial.creditGranted)} enrichment credit left.`,
    }),
    el('span', { class: 'spacer' }),
    el('button', {
      class: 'btn btn-small btn-primary',
      text: spent || soon ? 'Choose a plan' : 'See plans',
      onclick: () => go('#/billing'),
    }),
  );
}

function sidebar(): HTMLElement {
  const current = state.route.name === 'base' || state.route.name === 'changes' ? state.route.id : null;
  return el(
    'aside',
    { class: 'sidebar' },
    // The logo is the way back to the front door, here and on every signed-out screen.
    el('a', { class: 'brand', href: 'https://databasis.com', 'aria-label': 'databasis home' }, mark(20), el('span', { class: 'brand-name', text: 'databasis' })),
    el(
      'div',
      { class: 'sidebar-head' },
      el('h2', { text: 'Your data' }),
      el('button', { class: 'btn btn-small', onclick: () => go('#/ask'), text: '+ New' }),
    ),
    state.bases.length === 0
      ? el('p', {
          class: 'sidebar-foot',
          text: 'Nothing yet. Describe what you need and save it as a base.',
        })
      : el(
          'ul',
          { class: 'base-list' },
          ...state.bases.map((base) =>
            el(
              'li',
              {},
              el(
                'button',
                {
                  class: 'base-link',
                  'aria-current': base.id === current ? 'page' : null,
                  onclick: () => go(`#/base/${String(base.id)}`),
                },
                el('span', { class: 'name', text: base.name }),
                el('span', {
                  class: 'meta',
                  text:
                    `${plural(base.members, 'company', 'companies')}` +
                    (base.lastEvaluated === null ? '' : ` · ${age(base.lastEvaluated.ranAt)}`) +
                    (base.lastEvaluated?.resultsComplete === false ? ' · partial' : ''),
                }),
              ),
            ),
          ),
        ),
    el(
      'div',
      { class: 'sidebar-foot' },
      el('button', {
        class: 'account-link',
        'aria-current': state.route.name === 'account' ? 'page' : null,
        text: state.session?.user?.email ?? '',
        onclick: () => go('#/account'),
      }),
      el(
        'div',
        { style: 'display:flex;gap:8px;align-items:center;margin-top:4px' },
        el('button', {
          class: 'btn btn-small',
          text: (state.session?.plan?.name ?? 'Plan') + (state.session?.status === 'past_due' ? ' · past due' : ''),
          onclick: () => go('#/billing'),
        }),
        el('span', { style: 'flex:1' }),
        el('button', {
          class: 'btn btn-small',
          text: 'Sign out',
          onclick: () =>
            void run('Signing out', async () => {
              await api.signOut();
              // Everything, not just the session: a draft, a loaded base and its rows all
              // survived a sign-out, so the next person to use the browser saw the last
              // person's question and sample data on the ask screen.
              state.session = { signedIn: false };
              state.bases = [];
              state.draft = null;
              state.asked = '';
              state.fields = new Map();
              view = null;
              changesView = null;
              billingPlans = null;
              stopWatching();
              go('#/signin');
            }),
        }),
      ),
    ),
  );
}

function banner(err: ApiError): HTMLElement {
  // The planner being unconfigured is not a failure, it is a missing capability, and
  // saying so keeps the user working instead of retrying.
  const kind = err.code === 'planner_unavailable' ? 'banner-info' : 'banner-error';
  const title =
    err.code === 'planner_unavailable'
      ? 'Plain-English planning is off in this deployment'
      : err.code === 'offline'
        ? 'No connection'
        : 'That did not work';
  // A wall the user can walk through. An error whose remedy is "buy something" is the
  // one case where a banner should carry an action, because the alternative is a
  // sentence about a budget and no way to do anything about it.
  const priced = err.code === 'nothing_queued' || err.code === 'entitlement';
  return el(
    'div',
    { class: `banner ${kind}`, role: 'alert' },
    el('strong', { text: title }),
    err.message,
    err.path === undefined ? null : el('div', { class: 'diff', text: `at ${err.path}` }),
    priced && (state.session?.trial != null || state.session?.status === 'canceled')
      ? el('div', { style: 'margin-top:8px' },
          el('button', { class: 'btn btn-small btn-primary', text: 'Choose a plan', onclick: () => go('#/billing') }))
      : null,
  );
}

/* ------------------------------------------------------------ sign-in screen -- */

/**
 * Sign in: a password, or a link in the post (D34).
 *
 * Both doors are on one screen because which one a given person has is not knowable from
 * here — asking for the address first, then deciding what to show, is a round trip and a
 * second screen to explain the state of. The password fields are what the form does by
 * default; the link is a button under them that needs only the address.
 *
 * The failure sentence never separates "wrong password" from "no password" from "no
 * account". Telling a stranger which of those it was is how you learn which addresses are
 * worth a real attempt.
 */
function signInScreen(): HTMLElement {
  const email = el('input', {
    type: 'text',
    placeholder: 'you@company.com',
    'aria-label': 'Email address',
    autocomplete: 'email',
  });
  const password = el('input', {
    type: 'password',
    placeholder: 'Your password',
    'aria-label': 'Password',
    autocomplete: 'current-password',
  });
  const status = el('p', { class: 'sub' });

  const withEmail = (then: (address: string) => Promise<void>) => async (): Promise<void> => {
    const address = email.value.trim();
    if (address === '') {
      email.focus();
      status.textContent = 'Enter the email address you signed up with.';
      return;
    }
    await then(address);
  };

  const signIn = withEmail(async (address) => {
    if (password.value === '') {
      password.focus();
      status.textContent = 'Enter your password, or ask for a sign-in link instead.';
      return;
    }
    try {
      status.textContent = 'Signing in…';
      await api.signIn(address, password.value);
      // Same landing as a redeemed link, and by the same route, so there is one path
      // into the app rather than two that have to be kept agreeing.
      state.session = await api.session();
      await Promise.all([loadRegistry(), loadBases()]);
      go('#/ask');
    } catch (err) {
      password.value = '';
      status.textContent = (err as ApiError).message;
    }
  });

  const sendLink = withEmail(async (address) => {
    try {
      status.textContent = 'Sending…';
      const result = await api.requestLink(address);
      status.textContent =
        result.delivery === 'logged'
          ? `${result.message} Check the Worker console for the link.`
          : 'Check your email. The link works once and expires in 15 minutes.';
    } catch (err) {
      status.textContent = (err as ApiError).message;
    }
  });

  const box = el(
    'div',
    { class: 'gate' },
    el('a', { class: 'brand brand-plain', href: 'https://databasis.com', 'aria-label': 'databasis home' }, mark(24), el('span', { class: 'brand-name', text: 'databasis' })),
    el('h2', { text: 'Sign in' }),
    el('p', { class: 'lede', text: 'With your password, or with a link we email you.' }),
    el('label', { class: 'field-label', text: 'Email' }),
    email,
    el('label', { class: 'field-label', text: 'Password' }),
    password,
    el('button', { class: 'btn btn-primary', text: 'Sign in', onclick: () => void signIn() }),
    status,
    el(
      'div',
      { class: 'gate-alt' },
      el('button', {
        class: 'btn',
        text: 'Email me a sign-in link instead',
        onclick: () => void sendLink(),
      }),
      el('p', {
        class: 'note',
        text: 'Works for every account, and is how you get in if you have forgotten your password or never set one.',
      }),
    ),
    el('p', { class: 'note' }, 'New here? ', el('a', { href: '#/signup', text: 'Create your workspace' }), '. 14 days free, no card.'),
  );
  for (const field of [email, password]) {
    field.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') void signIn();
    });
  }
  queueMicrotask(() => email.focus());
  return box;
}

/**
 * Sign-up (D32, D34). Passwordless still works, so the password here is optional: it is
 * the sign-in form plus the workspace name that an email address cannot supply.
 *
 * The name and the password both ride on the emailed link, and both are used only if
 * redeeming it creates the workspace. So a password chosen here never becomes a usable
 * credential until the address it belongs to has been proved to be readable, and an
 * existing account that lands here by mistake simply signs in with nothing changed.
 */
function signUpScreen(): HTMLElement {
  const email = el('input', { type: 'text', placeholder: 'you@company.com', 'aria-label': 'Work email', autocomplete: 'email' });
  const org = el('input', { type: 'text', placeholder: 'Acme Heating & Air', 'aria-label': 'Workspace name', autocomplete: 'organization', maxlength: '80' });
  const password = el('input', {
    type: 'password',
    placeholder: `At least ${String(PASSWORD_MIN)} characters`,
    'aria-label': 'Password',
    autocomplete: 'new-password',
  });
  const status = el('p', { class: 'sub' });

  const submit = async (): Promise<void> => {
    if (email.value.trim() === '') {
      email.focus();
      return;
    }
    if (org.value.trim() === '') {
      org.focus();
      return;
    }
    if (password.value !== '' && password.value.length < PASSWORD_MIN) {
      // Checked here as well as on the server, because being told at the form beats
      // being told fifteen minutes later by a link that does not do what you expected.
      password.focus();
      status.textContent = `A password needs at least ${String(PASSWORD_MIN)} characters. Leave it empty to sign in by link instead.`;
      return;
    }
    try {
      status.textContent = 'Sending…';
      const result = await api.requestLink(email.value.trim(), org.value.trim(), password.value);
      status.textContent =
        result.delivery === 'logged'
          ? `${result.message} Check the Worker console for the link.`
          : 'Check your email. The link creates your workspace, works once, and expires in 15 minutes.';
    } catch (err) {
      status.textContent = (err as ApiError).message;
    }
  };

  const box = el(
    'div',
    { class: 'gate' },
    el('a', { class: 'brand brand-plain', href: 'https://databasis.com', 'aria-label': 'databasis home' }, mark(24), el('span', { class: 'brand-name', text: 'databasis' })),
    el('h2', { text: 'Create your workspace' }),
    el('p', { class: 'lede', text: '14 days free on Growth with $10 of enrichment credit. No card until you choose a plan.' }),
    el('label', { class: 'field-label', text: 'Work email' }),
    email,
    el('label', { class: 'field-label', text: 'Workspace name' }),
    org,
    el('p', { class: 'note', text: 'Usually your company. You can rename it later.' }),
    el('label', { class: 'field-label', text: 'Password (optional)' }),
    password,
    el('p', {
      class: 'note',
      text: 'Leave it empty to sign in by emailed link every time. You can set one later either way.',
    }),
    el('button', { class: 'btn btn-primary', text: 'Create workspace', onclick: () => void submit() }),
    status,
    el('p', { class: 'note' }, 'Already have a workspace? ', el('a', { href: '#/signin', text: 'Sign in' }), '.'),
  );
  for (const field of [email, org, password]) {
    field.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') void submit();
    });
  }
  queueMicrotask(() => email.focus());
  return box;
}

/**
 * Everything a signed-in session needs before a screen can mean anything.
 *
 * The field registry supplies every column label, datatype and freshness interval. It was
 * loaded only in `start()`, and only when the session was already signed in — but the
 * emailed link is the *only* way in, and at that moment the session is signed out. So the
 * normal path, clicking the link in the email, landed in an app whose headers read
 * `license_class`, whose numbers had no separators, and where nothing could ever be
 * marked stale.
 */
async function loadRegistry(): Promise<void> {
  const { fields } = await api.fields();
  state.fields = new Map(fields.map((spec) => [spec.key, spec]));
}

/** Redeem a link and land in the app. */
async function completeSignIn(token: string): Promise<void> {
  try {
    await api.redeem(token);
    state.session = await api.session();
    // The token is removed from the URL immediately: a sign-in link sitting in browser
    // history or a shared screenshot is a live credential until it is used.
    history.replaceState(null, '', '#/ask');
    await Promise.all([loadRegistry(), loadBases()]);
    await route();
  } catch (err) {
    state.error = err instanceof ApiError ? err : new ApiError(0, 'unknown', String(err));
    history.replaceState(null, '', '#/signin');
    render();
  }
}

/* --------------------------------------------------------------- ask screen -- */

const EXAMPLES = [
  'All HVAC companies in Virginia',
  'Plumbers in Richmond licensed before 2010',
  'Electrical contractors with no revenue cap on their licence',
  'Class A and C contractors doing HVAC',
];

function askScreen(): HTMLElement {
  const box = el('textarea', {
    placeholder: 'Describe the companies you need…',
    'aria-label': 'Describe the data you need',
    rows: '3',
  });
  // One source of truth for what is in the box. Seeding from the draft instead meant a
  // re-render — removing a criterion, say — silently reverted whatever the user had
  // retyped, which is the hazard CLAUDE.md warns about.
  box.value = state.asked;
  box.addEventListener('input', () => {
    state.asked = box.value;
  });

  const submit = async (): Promise<void> => {
    const request = box.value.trim();
    if (request === '') return;
    state.asked = request;
    await run('Reading your request', async () => {
      const { plan } = await api.plan(request);
      state.draft = { plan, request, preview: null };
      await refreshPreview();
    });
  };

  const screen = el(
    'div',
    { class: 'ask' },
    state.error === null ? null : banner(state.error),
    el('h2', { text: 'What data do you need?' }),
    el('p', {
      class: 'lede',
      text: 'Describe it the way you would to a colleague. You will see how it was read, how many companies match, and what anything missing would cost, before saving.',
    }),
    el(
      'div',
      { class: 'ask-box' },
      box,
      el(
        'div',
        { class: 'ask-actions' },
        el('span', { class: 'hint', text: state.busy ?? 'Enter to submit' }),
        state.busy === null ? null : el('span', { class: 'spinner' }),
        el('button', {
          class: 'btn btn-primary',
          text: 'Read it',
          disabled: state.busy !== null,
          onclick: submit,
        }),
      ),
    ),
    el(
      'div',
      { class: 'examples' },
      ...EXAMPLES.map((text) =>
        el('button', {
          class: 'example',
          text,
          onclick: () => {
            box.value = text;
            state.asked = text;
            box.focus();
          },
        }),
      ),
    ),
    state.draft === null ? null : draftPanels(state.draft),
  );

  // A rebuilt textarea is a different element, so focus has to be put back or the
  // caret jumps out of the box whenever anything re-renders.
  if (document.activeElement === document.body && state.asked !== '') {
    queueMicrotask(() => {
      box.focus();
      box.setSelectionRange(box.value.length, box.value.length);
    });
  }

  box.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  });
  return screen;
}

/* ------------------------------------------------ criteria review and edit -- */

/** A predicate in words, not in syntax. */
function readable(field: FieldSpec | undefined, cmp: string, value: unknown): string {
  const label = field?.label ?? field?.key ?? 'field';
  const v = Array.isArray(value)
    ? value.map((x) => String(x)).join(' or ')
    : typeof value === 'string'
      ? value
      : JSON.stringify(value);
  switch (cmp) {
    case 'eq': return `${label} is ${v}`;
    case 'ne': return `${label} is not ${v}`;
    case 'lt': return `${label} is under ${v}`;
    case 'lte': return `${label} is at most ${v}`;
    case 'gt': return `${label} is over ${v}`;
    case 'gte': return `${label} is at least ${v}`;
    case 'contains': return `${label} includes ${v}`;
    case 'in': return `${label} is one of ${v}`;
    case 'is_null': return `${label} has no value on record`;
    case 'is_not_null': return `${label} has a value on record`;
    case 'not_checked': return `${label} has never been checked`;
    default: return `${label} ${cmp} ${v}`;
  }
}

/** Walk the tree, yielding each predicate with the path the API accepts for edits. */
function walk(
  node: Plan['filters'],
  path: string,
  out: { predicate: Extract<NonNullable<Plan['filters']>, { op: 'cmp' }>; path: string; negated: boolean }[] = [],
  negated = false,
): typeof out {
  if (node === null) return out;
  switch (node.op) {
    case 'and':
    case 'or':
      node.children.forEach((child, i) => walk(child, `${path}.${node.op}[${String(i)}]`, out, negated));
      return out;
    case 'not':
      return walk(node.child, `${path}.not`, out, !negated);
    default:
      out.push({ predicate: node, path, negated });
      return out;
  }
}

function criteriaPanel(draft: NonNullable<State['draft']>): HTMLElement {
  const predicates = walk(draft.plan.filters, 'filters');
  const coverage = draft.preview?.coverage;

  return el(
    'div',
    { class: 'panel' },
    el(
      'div',
      { class: 'panel-head' },
      el('h3', { text: 'How it was read' }),
      el('span', { class: 'spacer' }),
      el('span', { class: 'col-age', text: `${plural(predicates.length, 'criterion', 'criteria')}` }),
    ),
    el(
      'div',
      { class: 'panel-body' },
      // Anything that could not be expressed goes FIRST and in red. Burying it below
      // the count is how a user comes to believe a list answers a question it does not.
      ...draft.plan.unsatisfiable.map((u) =>
        el(
          'div',
          { class: 'dropped' },
          el('div', { class: 'what', text: `"${u.text}" could not be applied` }),
          el('div', { class: 'why', text: u.reason }),
        ),
      ),
      predicates.length === 0
        ? el('p', { class: 'note', text: 'No filters: every company in the store.' })
        : el(
            'ul',
            { class: 'criteria' },
            ...predicates.map(({ predicate, path, negated }) => {
              const spec = state.fields.get(predicate.field);
              const cov = coverage?.fields.find((f) => f.field === predicate.field);
              return el(
                'li',
                { class: 'criterion' },
                el(
                  'div',
                  { class: 'body' },
                  el(
                    'div',
                    { class: 'expr' },
                    negated ? el('span', { class: 'tag tag-bound', text: 'NOT' }) : null,
                    ' ',
                    readable(spec, predicate.cmp, predicate.value),
                  ),
                  // A substitution must admit it is one, every time it is shown.
                  predicate.proxyFor === undefined
                    ? null
                    : el(
                        'div',
                        { class: 'note' },
                        el('span', { class: 'tag tag-proxy', text: 'stands in for' }),
                        ` "${predicate.proxyFor}", which we cannot measure directly`,
                      ),
                  spec?.isBound === true
                    ? el('div', {
                        class: 'note',
                        text: 'A statutory ceiling from licence class, not a measured figure.',
                      })
                    : null,
                  cov === undefined || cov.trustworthy
                    ? null
                    : el(
                        'div',
                        { class: 'note' },
                        el('span', { class: 'tag tag-thin', text: `${(cov.ratio * 100).toFixed(0)}% populated` }),
                        cov.costToFill === null
                          ? ': nothing we can buy will fill the rest'
                          : `: filling it costs ${money(cov.costToFill)}`,
                      ),
                ),
                el('button', {
                  class: 'btn btn-small btn-danger',
                  text: 'Remove',
                  title: 'Remove this criterion',
                  onclick: () =>
                    void run('Updating', async () => {
                      const { plan } = await api.edit({ plan: draft.plan, op: 'remove', path });
                      draft.plan = plan;
                      await refreshPreview();
                    }),
                }),
              );
            }),
          ),
    ),
  );
}

function draftPanels(draft: NonNullable<State['draft']>): DocumentFragment {
  const preview = draft.preview;
  return frag(
    el('div', { style: 'height:22px' }),
    criteriaPanel(draft),
    preview === null
      ? el('div', { class: 'panel' }, el('div', { class: 'panel-body' }, el('div', { class: 'skeleton' })))
      : el(
          'div',
          { class: 'panel' },
          el('div', { class: 'panel-head' }, el('h3', { text: 'What you would get' })),
          el(
            'div',
            { class: 'panel-body' },
            el(
              'div',
              { class: 'numbers' },
              el(
                'div',
                { class: `number${preview.resultsComplete ? '' : ' approx'}` },
                el('span', { class: 'n', text: preview.matched.toLocaleString() }),
                el('span', { class: 'k', text: 'companies' }),
              ),
              preview.enrichment.estimatedCost === 0
                ? null
                : el(
                    'div',
                    { class: 'number' },
                    el('span', { class: 'n cost', text: money(preview.enrichment.estimatedCost) }),
                    el('span', { class: 'k', text: 'to fill the gaps' }),
                  ),
            ),
            el('p', {
              class: `summary${preview.resultsComplete ? '' : ' partial'}`,
              text: preview.summary,
            }),
            preview.sample.length === 0
              ? null
              : sampleTable(preview.sample),
            el(
              'div',
              { style: 'display:flex;gap:8px;margin-top:14px;align-items:center' },
              el('button', {
                class: 'btn btn-primary',
                text: 'Save as a base',
                disabled: state.busy !== null,
                onclick: () => saveDraft(draft),
              }),
              el('span', { class: 'col-age', text: 'Saving evaluates it once. Nothing is charged.' }),
            ),
          ),
        ),
  );
}

function sampleTable(sample: Record<string, unknown>[]): HTMLElement {
  const keys = Object.keys(sample[0] ?? {}).filter((k) => k !== 'id' && k !== 'custom');
  return el(
    'div',
    { class: 'table-wrap', style: 'margin-top:12px;border:1px solid var(--line);border-radius:6px' },
    el(
      'table',
      { class: 'rows' },
      el(
        'thead',
        {},
        el('tr', {}, ...keys.map((k) => el('th', { text: state.fields.get(k)?.label ?? k }))),
      ),
      el(
        'tbody',
        {},
        ...sample.map((row) =>
          el(
            'tr',
            {},
            ...keys.map((k) => {
              const value = row[k];
              return el(
                'td',
                {},
                value === null || value === undefined
                  ? el('span', { class: 'unchecked', text: 'not checked' })
                  : String(value),
              );
            }),
          ),
        ),
      ),
    ),
  );
}

/**
 * Ask for one line of text in the app's own dialog, never the browser's.
 *
 * The browser's prompt is unstyled, blocks the whole tab, and looks like something went
 * wrong. This is the same <dialog> the add-column form uses: the field is focused with
 * its text selected, Enter submits, Escape cancels, and nothing happens on an empty
 * answer.
 */
function askForName(opts: {
  title: string;
  sub: string;
  initial: string;
  action: string;
  onSubmit: (name: string) => void;
}): void {
  const input = el('input', { type: 'text', id: 'ask-name', value: opts.initial, maxlength: '80', style: 'width:100%' });
  const dialog = el(
    'dialog',
    {},
    el(
      'form',
      {
        onsubmit: (e: Event) => {
          e.preventDefault();
          const name = input.value.trim();
          if (name === '') {
            input.focus();
            return;
          }
          dialog.close();
          opts.onSubmit(name);
        },
      },
      el(
        'div',
        { class: 'dialog-body' },
        el('h3', { text: opts.title }),
        el('p', { class: 'sub', text: opts.sub }),
        el('label', { class: 'field-label', for: 'ask-name', text: 'Name' }),
        input,
      ),
      el(
        'div',
        { class: 'dialog-foot' },
        el('button', { class: 'btn', type: 'button', text: 'Cancel', onclick: () => dialog.close() }),
        el('button', { class: 'btn btn-primary', type: 'submit', text: opts.action }),
      ),
    ),
  );
  document.body.appendChild(dialog);
  dialog.addEventListener('close', () => dialog.remove());
  dialog.showModal();
  input.focus();
  input.select();
}

async function saveDraft(draft: NonNullable<State['draft']>): Promise<void> {
  askForName({
    title: 'Save this base',
    sub: 'The question and its criteria are saved with it, and it is kept current from here on. You can rename it later.',
    initial: draft.request.slice(0, 60),
    action: 'Save base',
    onSubmit: (name) =>
      void run('Saving', async () => {
        const { base } = await api.createBase(name, draft.plan, draft.request);
        state.draft = null;
        await loadBases();
        go(`#/base/${String(base.id)}`);
      }),
  });
}

/* -------------------------------------------------------------- base screen -- */

interface BaseView {
  base: { id: number; name: string; request: string | null; plan: Plan };
  members: number;
  columns: CustomColumn[];
  evaluations: { ranAt: string; matched: number; entered: number; exited: number; resultsComplete: boolean }[];
  rows: Row[];
  total: number;
  fields: string[];
  offset: number;
  pending: Awaited<ReturnType<typeof api.pending>> | null;
  /** Queued enrichment for this base, polled while any of it is still working. */
  progress: EnrichmentProgress | null;
}

let progressTimer: number | null = null;

/** Stop the enrichment poll. Called whenever the base screen is left, for any reason. */
function stopWatching(): void {
  if (progressTimer !== null) window.clearTimeout(progressTimer);
  progressTimer = null;
}

/**
 * Poll the queue until nothing is pending, then reload the rows so the new values
 * appear. Held in `view` and drawn by `render`, never written into the DOM directly:
 * `run` re-renders when it finishes and would wipe anything else.
 */
async function watchProgress(id: number): Promise<void> {
  stopWatching();
  // Bound to the route, not just to `view`: leaving for the ask screen does not clear
  // `view`, so the poll used to keep hitting the API every four seconds for as long as
  // the tab stayed open.
  if (state.route.name !== 'base' || state.route.id !== id) return;
  if (view === null || view.base.id !== id) return;
  try {
    const progress = await api.enrichment(id);
    if (view === null || view.base.id !== id) return;
    const wasActive = view.progress?.active ?? false;
    view.progress = progress;
    render();
    if (progress.active) {
      progressTimer = window.setTimeout(() => void watchProgress(id), 4000);
    } else if (wasActive) {
      // Something finished since the last look: the table is out of date.
      await loadBase(id, view.offset);
    }
  } catch {
    // One dropped poll is not the end of the work. Keep watching, more slowly, rather
    // than leaving a spinner that never resolves and a table that never reloads.
    if (state.route.name === 'base' && state.route.id === id) {
      progressTimer = window.setTimeout(() => void watchProgress(id), 15_000);
    }
  }
}

let view: BaseView | null = null;
/**
 * The changes screen's data.
 *
 * In module state rather than painted straight into the DOM: `run` re-renders when it
 * finishes, so anything written outside `render` is wiped the moment the busy flag
 * clears — which is exactly what happened the first time this screen was built.
 */
let changesView: {
  id: number;
  name: string;
  data: Awaited<ReturnType<typeof api.changes>>;
} | null = null;
const PAGE = 50;

async function loadBase(id: number, offset = 0): Promise<void> {
  const [detail, page] = await Promise.all([api.base(id), api.rows(id, PAGE, offset)]);
  // The user may have moved on while this was in flight. Without this check, opening a
  // slow base and then a fast one left the slow one's rows on screen under the fast
  // one's name in the sidebar — and "Fill in now" then spent against the wrong base.
  if (state.route.name !== 'base' || state.route.id !== id) return;
  const progress = view !== null && view.base.id === id ? view.progress : null;
  view = { ...detail, ...page, offset, pending: null, progress };
  render();
  // Work already queued for this base, e.g. from an earlier visit, keeps being watched.
  if (progress === null) void watchProgress(id);
  // Pending work is a second request on purpose: it is slower and the table should not
  // wait for a cost estimate.
  try {
    const pending = await api.pending(id);
    if (view !== null && view.base.id === id && state.route.name === 'base' && state.route.id === id) {
      view.pending = pending;
      render();
    }
  } catch {
    /* the table is still usable without it */
  }
}

function baseScreen(): HTMLElement {
  if (view === null) {
    // A base that 403s or a dropped connection used to leave an animated skeleton for
    // ever, because the error banner sat below this early return.
    return state.error === null
      ? el('div', { class: 'pad' }, el('div', { class: 'skeleton' }))
      : el(
          'div',
          { class: 'pad' },
          banner(state.error),
          el('button', {
            class: 'btn',
            style: 'margin-top:10px',
            text: 'Try again',
            onclick: () => void route(),
          }),
        );
  }
  const v = view;
  const lastEval = v.evaluations[0];

  return el(
    'div',
    {},
    el(
      'div',
      { class: 'topbar' },
      el('h1', { text: v.base.name }),
      v.base.request === null ? null : el('span', { class: 'request', text: `"${v.base.request}"` }),
      el('span', { class: 'spacer' }),
      lastEval?.resultsComplete === false
        ? el('span', { class: 'tag tag-thin', text: 'partial coverage' })
        : null,
      el('button', {
        class: 'btn btn-small',
        text: 'Changes',
        onclick: () => go(`#/changes/${String(v.base.id)}`),
      }),
      el('button', {
        class: 'btn btn-small',
        text: 'Add column',
        onclick: () => columnDialog(v),
      }),
      el('button', {
        class: 'btn btn-small',
        text: 'Export',
        onclick: () => exportDialog(v),
      }),
      el('button', {
        class: 'btn btn-small',
        text: state.busy === null ? 'Re-check' : 'Checking…',
        disabled: state.busy !== null,
        onclick: () =>
          void run('Re-checking', async () => {
            await api.evaluate(v.base.id);
            await Promise.all([loadBases(), loadBase(v.base.id, v.offset)]);
          }),
      }),
      el('button', { class: 'btn btn-small', text: 'Rename', onclick: () => renameBase(v) }),
    ),
    el(
      'div',
      { class: 'screen' },
      state.error === null ? null : el('div', { class: 'pad' }, banner(state.error)),
      v.pending === null ? null : pendingPanel(v),
      v.total === 0 ? emptyState(v) : resultsTable(v),
    ),
  );
}

/** Enrichment and backfill: what is missing, what it costs, nothing started silently. */
function pendingPanel(v: BaseView): HTMLElement | null {
  const p = v.pending!;
  const jobs = p.enrichment.jobs;
  const backfill = p.backfill.filter((b) => b.entityIds > 0);
  const progress = v.progress !== null && v.progress.total > 0 ? progressPanel(v) : null;
  if (jobs.length === 0 && backfill.length === 0) return progress === null ? null : el('div', { class: 'pad', style: 'padding-bottom:0' }, progress);

  const runnable = jobs.filter((j) => j.runnable);
  const unrunnable = jobs.filter((j) => !j.runnable);
  const runnableBackfill = backfill.filter((b) => b.runnable);
  const runnableCost =
    runnable.reduce((s, j) => s + j.estimatedCost, 0) + runnableBackfill.reduce((s, b) => s + b.estimatedCost, 0);
  const total = p.enrichment.estimatedCost + backfill.reduce((s, b) => s + b.estimatedCost, 0);
  const inFlight = v.progress?.active ?? false;
  return el(
    'div',
    { class: 'pad', style: 'padding-bottom:0' },
    progress,
    el(
      'div',
      { class: 'banner banner-warn' },
      el('strong', { text: `Some columns are not filled in yet: about ${money(total)} to complete` }),
      el(
        'div',
        {},
        ...jobs.map((job) =>
          el('div', {
            class: 'diff',
            text:
              `${job.fields.join(', ')}: ${plural(job.entityIds, 'company', 'companies')}, ${money(job.estimatedCost)}` +
              (job.runnable ? '' : ' (no provider for this yet)'),
          }),
        ),
        ...backfill.map((b) =>
          el('div', {
            class: 'diff',
            text:
              `${b.column.name}: ${plural(b.entityIds, 'company', 'companies')}, ${money(b.estimatedCost)}` +
              (b.alreadyAnswered > 0 ? ` (${String(b.alreadyAnswered)} already answered)` : '') +
              (b.runnable ? '' : ' (no model for this yet)'),
          }),
        ),
      ),
      runnable.length === 0 && runnableBackfill.length === 0
        ? null
        : el('button', {
            class: 'btn btn-primary',
            style: 'margin-top:8px',
            disabled: inFlight || state.busy !== null,
            text: inFlight ? 'Filling in…' : `Fill in now, about ${money(runnableCost)}`,
            onclick: () =>
              void run('Queueing', async () => {
                const result = await api.enrich(v.base.id);
                if (result.queued === 0) {
                  // The one flow that involves money must never look like a dead button.
                  // A budget wall or a paused subscription comes back as a refusal with a
                  // reason, and that reason is the whole answer.
                  //
                  // The session is re-read first: a refusal usually means the credit just
                  // ran out, and the trial strip at the top of the screen is the thing
                  // that says so. Leaving it stale would have it cheerfully reporting a
                  // balance in the same breath as the refusal for spending it.
                  state.session = await api.session();
                  const reason = result.refused[0]?.reason;
                  throw new ApiError(
                    409,
                    'nothing_queued',
                    reason ?? 'There was nothing left to fill in. It may already be queued.',
                  );
                }
                if (view !== null && view.base.id === v.base.id) {
                  view.progress = { pending: result.queued, running: 0, done: 0, skipped: 0, failed: 0, dead: 0, total: result.queued, active: true, errors: [] };
                }
                void watchProgress(v.base.id);
              }),
          }),
      // Honest about what a click cannot do: the estimate for the rest is what it will
      // cost once a provider exists, and custom columns are not on the queue yet.
      el('div', {
        class: 'note',
        style: 'margin-top:6px',
        text:
          (unrunnable.length > 0 ? `${unrunnable.map((j) => j.fields.join(', ')).join('; ')}: no provider is configured, so these stay unfilled for now. ` : '') +
          (backfill.some((b) => !b.runnable) ? 'Custom columns need a model credential, which this deployment lacks. ' : '') +
          'Each company is checked against the shared cache first, so the real cost is usually lower than the estimate, and the monthly budget stops it before it runs over.',
      }),
    ),
  );
}

/** Where queued enrichment stands. Polled every few seconds while anything is working. */
function progressPanel(v: BaseView): HTMLElement {
  const g = v.progress!;
  const finished = g.done + g.skipped + g.dead;
  const working = g.pending + g.running + g.failed;
  return el(
    'div',
    { class: g.active ? 'banner banner-info' : g.dead > 0 ? 'banner banner-warn' : 'banner banner-info', style: 'margin-bottom:8px' },
    el(
      'strong',
      { text: `${g.active ? 'Filling in' : 'Filled in'}: ${String(finished)} of ${String(g.total)} done` },
    ),
    g.active ? el('span', { class: 'spinner', style: 'margin-left:8px' }) : null,
    el('div', {
      class: 'diff',
      text:
        `${plural(g.done, 'company', 'companies')} updated, ${String(g.skipped)} already fresh from the shared cache` +
        (g.failed > 0 ? `, ${String(g.failed)} retrying` : '') +
        (g.dead > 0 ? `, ${String(g.dead)} gave up` : '') +
        (working > 0 ? `, ${String(working)} to go` : ''),
    }),
    ...g.errors.map((e) => el('div', { class: 'diff', text: `${e.fields.join(', ')}: ${e.error}` })),
  );
}

function emptyState(v: BaseView): HTMLElement {
  const untrustworthy = v.pending?.coverage.untrustworthyFilters ?? [];
  // Distinguishing "nothing matches" from "we have not collected the data your filter
  // needs" is the difference between a dead end and a next step.
  return el(
    'div',
    { class: 'empty' },
    el('h3', { text: 'No companies match yet' }),
    untrustworthy.length === 0
      ? el('p', { text: 'The filters are all on data we have, so this is a real answer: nothing in the store matches. Try widening a criterion.' })
      : el('p', {
          text: `This filters on ${untrustworthy.join(' and ')}, which we have not collected for these companies yet, so the answer is "we do not know", not "none".`,
        }),
    el('button', { class: 'btn', text: 'Edit the criteria', onclick: () => editPlanOf(v) }),
  );
}

/* -------------------------------------------------------------------- table -- */

function resultsTable(v: BaseView): HTMLElement {
  const specs = v.fields.map((key) => state.fields.get(key));

  const head = el(
    'tr',
    {},
    el('th', { text: '#' }),
    ...v.fields.map((key, i) => {
      const spec = specs[i];
      // Column-level freshness: the oldest observation in the column, against the
      // field's own policy. A licence date has no policy and never reads as stale.
      const ages = v.rows
        .map((r) => r.values[key]?.observedAt)
        .filter((x): x is string => x !== undefined)
        .map((iso) => Date.now() - new Date(iso).getTime());
      const oldest = ages.length === 0 ? null : Math.max(...ages);
      const policy = intervalMs(spec?.freshness ?? null);
      const stale = oldest !== null && policy !== null && oldest > policy;
      return el(
        'th',
        {
          title:
            spec === undefined
              ? 'We do not know how often this is refreshed.'
              : spec.freshness === null
                ? 'This value does not go stale.'
                : `Refreshed every ${spec.freshness}`,
        },
        el(
          'div',
          { class: 'col' },
          spec?.label ?? key,
          spec?.costClass === 'metered' ? el('span', { class: 'tag tag-metered', text: '$' }) : null,
          spec?.costClass === 'ai' ? el('span', { class: 'tag tag-ai', text: 'AI' }) : null,
          spec?.isBound === true ? el('span', { class: 'tag tag-bound', text: 'cap' }) : null,
          oldest === null ? null : el('span', { class: `col-age${stale ? ' stale' : ''}`, text: age(new Date(Date.now() - oldest).toISOString()) }),
        ),
      );
    }),
    ...v.columns.map((column) =>
      el(
        'th',
        { title: column.prompt },
        el(
          'div',
          { class: 'col' },
          column.name,
          el('span', { class: 'tag tag-ai', text: 'AI' }),
          el('button', {
            class: 'btn btn-small',
            text: '×',
            title: 'Remove this column',
            onclick: () =>
              void run('Removing column', async () => {
                await api.removeColumn(v.base.id, column.id);
                await loadBase(v.base.id, v.offset);
              }),
          }),
        ),
      ),
    ),
  );

  const body = el(
    'tbody',
    {},
    ...v.rows.map((row, i) =>
      el(
        'tr',
        {},
        el('td', { class: 'num', text: String(v.offset + i + 1) }),
        ...v.fields.map((key) => cell(row, key)),
        ...v.columns.map((column) => customCell(row, column)),
      ),
    ),
  );

  return el(
    'div',
    {},
    el(
      'div',
      { class: 'table-wrap' },
      el('table', { class: 'rows' }, el('thead', {}, head), body),
    ),
    el(
      'div',
      { class: 'pad', style: 'display:flex;gap:10px;align-items:center' },
      el('span', {
        class: 'col-age',
        text: `${String(v.offset + 1)}–${String(v.offset + v.rows.length)} of ${plural(v.total, 'company', 'companies')}`,
      }),
      el('span', { class: 'spacer', style: 'flex:1' }),
      el('button', {
        class: 'btn btn-small',
        text: 'Previous',
        disabled: v.offset === 0,
        onclick: () => void run('Loading', () => loadBase(v.base.id, Math.max(0, v.offset - PAGE))),
      }),
      el('button', {
        class: 'btn btn-small',
        text: 'Next',
        disabled: v.offset + v.rows.length >= v.total,
        onclick: () => void run('Loading', () => loadBase(v.base.id, v.offset + PAGE)),
      }),
    ),
  );
}

/**
 * One cell.
 *
 * Three states, visually distinct, because collapsing them is the failure that makes a
 * table uninterpretable: never checked, checked-and-empty, and a value. Clicking opens
 * the provenance popover.
 */
function cell(row: Row, key: string): HTMLElement {
  const spec = state.fields.get(key);
  const held = row.values[key] as CellValue | undefined;
  const numeric = spec?.datatype === 'integer' || spec?.datatype === 'numeric';

  if (held === undefined) {
    return el(
      'td',
      { class: numeric ? 'num' : undefined },
      el('span', { class: 'unchecked', text: 'not checked' }),
    );
  }
  if (held.value === null) {
    return el(
      'td',
      { class: numeric ? 'num' : undefined, title: 'Checked, and there is genuinely no value.' },
      el('button', {
        class: 'cell empty-value',
        // A word, not a dash: the tooltip says what it means and the muted styling says
        // it is not a value, so the cell may as well read as English.
        text: 'none',
        onclick: (e: Event) => openProvenance(e, row.id, key),
      }),
    );
  }

  const policy = intervalMs(spec?.freshness ?? null);
  const old = policy !== null && Date.now() - new Date(held.observedAt).getTime() > policy;
  return el(
    'td',
    { class: numeric ? 'num' : undefined },
    el(
      'button',
      {
        class: 'cell',
        onclick: (e: Event) => openProvenance(e, row.id, key),
        title: `${held.source} · ${stamp(held.observedAt)}`,
      },
      display(held.value, spec?.datatype),
      old ? el('span', { class: 'stale-mark', title: 'Older than this field’s refresh policy' }) : null,
    ),
  );
}

function customCell(row: Row, column: CustomColumn): HTMLElement {
  const held = row.custom[column.name];
  if (held === undefined) {
    return el('td', {}, el('span', { class: 'unchecked', text: 'not computed' }));
  }
  if (held.value === null) {
    return el(
      'td',
      { title: 'Computed, and the page does not say.' },
      el('span', { class: 'empty-value', text: 'none' }),
    );
  }
  return el(
    'td',
    { title: held.evidence ?? undefined },
    String(held.value),
    held.evidence === null ? null : el('span', { class: 'stale-mark', style: 'background:var(--teal)' }),
  );
}

/* ---------------------------------------------------------------- provenance -- */

let openPop: HTMLElement | null = null;

function closePop(): void {
  openPop?.remove();
  openPop = null;
}

async function openProvenance(event: Event, entityId: number, field: string): Promise<void> {
  closePop();
  const anchor = event.currentTarget as HTMLElement;
  const box = anchor.getBoundingClientRect();

  const pop = el(
    'div',
    { class: 'pop', role: 'dialog', 'aria-label': `Where ${field} came from` },
    el(
      'div',
      { class: 'pop-head' },
      el('span', { class: 'what', text: state.fields.get(field)?.label ?? field }),
      el('span', { class: 'spacer', style: 'flex:1' }),
      el('button', { class: 'btn btn-small', text: 'Close', onclick: closePop }),
    ),
    el('div', { class: 'pop-body' }, el('div', { class: 'skeleton' })),
  );
  pop.style.left = `${String(Math.min(box.left, window.innerWidth - 356))}px`;
  pop.style.top = `${String(box.bottom + window.scrollY + 5)}px`;
  document.body.appendChild(pop);
  openPop = pop;

  const body = pop.querySelector('.pop-body')!;
  try {
    const { observations, changes } = await api.provenance(entityId, field);
    fill(
      body,
      ...observations.map((obs) =>
        el(
          'div',
          { class: `obs${obs.superseded_at === null ? '' : ' superseded'}` },
          el('div', {
            class: 'v',
            text:
              obs.value === null
                ? 'none (checked, nothing there)'
                : display(obs.value, state.fields.get(field)?.datatype),
          }),
          el('div', {
            class: 'who',
            text: `${obs.source_name} · confidence ${Number(obs.confidence).toFixed(2)}`,
          }),
          el('div', { class: 'when', text: stamp(obs.observed_at) + (obs.superseded_at === null ? '' : ` · replaced ${age(obs.superseded_at)} ago`) }),
          obs.evidence === null ? null : el('div', { class: 'ev', text: obs.evidence }),
          obs.snapshot_hash === null
            ? null
            : el('div', { class: 'when', text: `snapshot ${obs.snapshot_hash.slice(0, 12)}…` }),
        ),
      ),
      changes.length === 0
        ? null
        : el(
            'div',
            { style: 'margin-top:10px;padding-top:10px;border-top:1px solid var(--line)' },
            el('div', { class: 'k', style: 'margin-bottom:5px', text: 'Changes' }),
            ...changes.slice(0, 5).map((change) =>
              el(
                'div',
                { class: 'diff', style: 'margin-bottom:3px' },
                el('span', { class: 'from', text: change.old_value === null ? '(none)' : String(change.old_value) }),
                ' → ',
                el('span', { class: 'to', text: change.new_value === null ? '(none)' : String(change.new_value) }),
                el('span', { class: 'when', text: ` ${age(change.detected_at)}` }),
              ),
            ),
          ),
    );
  } catch (err) {
    fill(body, banner(err as ApiError));
  }
}

document.addEventListener('click', (event) => {
  if (openPop !== null && !openPop.contains(event.target as Node)) {
    const target = event.target as HTMLElement;
    if (!target.classList.contains('cell')) closePop();
  }
});
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closePop();
});

/* ------------------------------------------------------------------ dialogs -- */

function columnDialog(v: BaseView): void {
  const name = el('input', { type: 'text', id: 'col-name', placeholder: 'Offers financing?' });
  const prompt = el('textarea', {
    id: 'col-prompt',
    rows: '3',
    placeholder: 'Does this company offer financing or payment plans on new installations?',
  });
  const type = el(
    'select',
    { id: 'col-type' },
    el('option', { value: 'boolean', text: 'Yes / no' }),
    el('option', { value: 'text', text: 'Short text' }),
    el('option', { value: 'number', text: 'Number' }),
  );
  const cost = el('p', {
    class: 'sub',
    text: `About ${money(v.members * 0.00275)} to answer for ${plural(v.members, 'company', 'companies')}.`,
  });

  const dialog = el(
    'dialog',
    {},
    el(
      'div',
      { class: 'dialog-body' },
      el('h3', { text: 'Add a column' }),
      el('p', { class: 'sub', text: 'Ask a question in plain English. It is answered per company from their website, with the sentence that justified it.' }),
      el('label', { class: 'field-label', for: 'col-name', text: 'Column name' }),
      name,
      el('label', { class: 'field-label', style: 'margin-top:12px', for: 'col-prompt', text: 'The question' }),
      prompt,
      el('label', { class: 'field-label', style: 'margin-top:12px', for: 'col-type', text: 'Answer shape' }),
      type,
      el('div', { style: 'height:12px' }),
      cost,
    ),
    el(
      'div',
      { class: 'dialog-foot' },
      el('button', { class: 'btn', text: 'Cancel', onclick: () => dialog.close() }),
      el('button', {
        class: 'btn btn-primary',
        text: 'Add column',
        onclick: () => {
          if (name.value.trim() === '' || prompt.value.trim() === '') return;
          dialog.close();
          void run('Adding column', async () => {
            await api.addColumn(v.base.id, {
              name: name.value.trim(),
              prompt: prompt.value.trim(),
              outputType: type.value,
            });
            await loadBase(v.base.id, v.offset);
          });
        },
      }),
    ),
  );
  document.body.appendChild(dialog);
  dialog.addEventListener('close', () => dialog.remove());
  dialog.showModal();
  name.focus();
}

/**
 * Export a base.
 *
 * Provenance is offered rather than assumed, and the consequence of leaving it off is
 * stated: without source and date columns, an empty cell cannot be told apart from one we
 * never checked. The notice is not optional — D11's obligations are about presentation, so
 * a file that leaves without them is the non-compliant artefact.
 */
function exportDialog(v: BaseView): void {
  const provenance = el('input', { type: 'checkbox', checked: true });
  const status = el('div', { class: 'sub', style: 'min-height:38px' });
  const links = el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap' });

  const dialog = el(
    'dialog',
    {},
    el(
      'div',
      { class: 'dialog-body' },
      el('h3', { text: 'Export this base' }),
      el('p', {
        class: 'sub',
        text: `${plural(v.members, 'company', 'companies')} as CSV, plus a notice describing the sources and what the data may be used for.`,
      }),
      el(
        'label',
        { style: 'display:flex;gap:8px;align-items:flex-start' },
        provenance,
        el(
          'span',
          {},
          el('strong', { text: 'Include source and date for every value' }),
          el('div', {
            class: 'sub',
            text: 'Without these, an empty cell cannot be told apart from one we have never checked.',
          }),
        ),
      ),
      el('div', { style: 'height:12px' }),
      status,
      links,
    ),
    el(
      'div',
      { class: 'dialog-foot' },
      el('button', { class: 'btn', text: 'Close', onclick: () => dialog.close() }),
      el('button', {
        class: 'btn btn-primary',
        text: 'Export',
        onclick: async (event: Event) => {
          const button = event.currentTarget as HTMLButtonElement;
          button.disabled = true;
          fill(status, el('span', { class: 'spinner' }), ' Building the file…');
          try {
            const result = await api.exportBase(v.base.id, provenance.checked);
            fill(
              status,
              el('div', { text: `${plural(result.rows, 'row')} ready. Links expire in 24 hours.` }),
              ...result.omitted.map((o) =>
                el('div', { class: 'tag tag-thin', text: `${o.field} withheld: ${o.reason}` }),
              ),
            );
            fill(
              links,
              el('a', { class: 'btn btn-primary', href: result.dataUrl, download: result.filename, text: 'Download CSV' }),
              el('a', { class: 'btn', href: result.noticeUrl, text: 'Download notice' }),
            );
          } catch (err) {
            fill(status, banner(err as ApiError));
          } finally {
            button.disabled = false;
          }
        },
      }),
    ),
  );
  document.body.appendChild(dialog);
  dialog.addEventListener('close', () => dialog.remove());
  dialog.showModal();
}

function renameBase(v: BaseView): void {
  askForName({
    title: 'Rename this base',
    sub: 'Only the name changes. The criteria, members and history stay as they are.',
    initial: v.base.name,
    action: 'Rename',
    onSubmit: (next) => {
      if (next === v.base.name) return;
      void run('Renaming', async () => {
        await api.patchBase(v.base.id, { name: next });
        await Promise.all([loadBases(), loadBase(v.base.id, v.offset)]);
      });
    },
  });
}

function editPlanOf(v: BaseView): void {
  // Editing a saved base reuses the ask screen's review panel: one editor, so a
  // criterion behaves the same whether it is new or six months old.
  state.draft = { plan: v.base.plan, request: v.base.request ?? '', preview: null };
  go('#/ask');
  void refreshPreview();
}

/* ------------------------------------------------------------- changes view -- */

async function loadChanges(id: number): Promise<void> {
  const [detail, data] = await Promise.all([api.base(id), api.changes(id)]);
  changesView = { id, name: detail.base.name, data };
}

function changesScreen(): HTMLElement {
  if (changesView === null) {
    return state.error === null
      ? el('div', { class: 'pad' }, el('div', { class: 'skeleton' }))
      : el('div', { class: 'pad' }, banner(state.error));
  }
  const { id, name, data: changes } = changesView;
  const membership = changes.membership;

  const items: HTMLElement[] = [];
  for (const entry of membership?.entered ?? []) {
    items.push(
      el(
        'li',
        {},
        el('span', { class: 'kind kind-entered', text: 'Entered' }),
        el('span', { class: 'what', text: `Company #${String(entry.entityId)} now matches` }),
        el('span', { class: 'when', text: age(entry.at) }),
      ),
    );
  }
  for (const entry of membership?.exited ?? []) {
    items.push(
      el(
        'li',
        {},
        el('span', { class: 'kind kind-exited', text: 'Left' }),
        el('span', { class: 'what', text: `Company #${String(entry.entityId)} no longer matches` }),
        el('span', { class: 'when', text: age(entry.at) }),
      ),
    );
  }
  // First-time observations are collapsed per company. A newly ingested company
  // produces one "appeared" event per field, and fifteen identical-looking rows saying
  // a company was recorded is not news — it buries the two rows that are.
  const firstSeen = new Map<number, { name: string; fields: string[]; at: string }>();
  for (const change of changes.fields) {
    if (change.kind !== 'appeared') continue;
    const label = state.fields.get(change.field_key)?.label ?? change.field_key;
    const group = firstSeen.get(change.entity_id);
    if (group === undefined) {
      firstSeen.set(change.entity_id, {
        name: change.name ?? `#${String(change.entity_id)}`,
        fields: [label],
        at: change.detected_at,
      });
    } else group.fields.push(label);
  }

  for (const [, group] of firstSeen) {
    items.push(
      el(
        'li',
        {},
        el('span', { class: 'kind kind-entered', text: 'Recorded' }),
        el(
          'span',
          { class: 'what' },
          el('div', { text: group.name }),
          el('div', {
            class: 'diff',
            text: `first seen, ${plural(group.fields.length, 'field')}: ${group.fields.slice(0, 6).join(', ')}${group.fields.length > 6 ? '…' : ''}`,
          }),
        ),
        el('span', { class: 'when', text: age(group.at) }),
      ),
    );
  }

  for (const change of changes.fields) {
    if (change.kind === 'appeared') continue;
    const spec = state.fields.get(change.field_key);
    // A jsonb null is "checked, and there is nothing there" — distinct from an absent
    // value, and rendering both as "(none)" throws that distinction away.
    const side = (value: unknown): string =>
      value === null ? 'no value on record' : display(value, spec?.datatype);
    items.push(
      el(
        'li',
        {},
        el('span', { class: 'kind kind-changed', text: change.kind }),
        el(
          'span',
          { class: 'what' },
          el('div', { text: `${change.name ?? `#${String(change.entity_id)}`} · ${spec?.label ?? change.field_key}` }),
          el(
            'div',
            { class: 'diff' },
            el('span', { class: 'from', text: side(change.old_value) }),
            ' → ',
            el('span', { class: 'to', text: side(change.new_value) }),
          ),
        ),
        el('span', { class: 'when', text: age(change.detected_at) }),
      ),
    );
  }

  return el(
    'div',
    {},
    el(
      'div',
      { class: 'topbar' },
      el('h1', { text: `${name}: what changed` }),
      el('span', { class: 'spacer' }),
      el('button', { class: 'btn btn-small', text: 'Back to table', onclick: () => go(`#/base/${String(id)}`) }),
    ),
    el(
      'div',
      { class: 'screen pad' },
      membership === null
        ? el(
            'div',
            { class: 'banner banner-info' },
            el('strong', { text: 'No comparison yet' }),
            'This base has been checked once, so there is nothing to compare against. Changes appear after the next check.',
          )
        : null,
      items.length === 0
        ? el(
            'div',
            { class: 'empty' },
            el('h3', { text: 'Nothing has changed' }),
            el('p', { text: 'No company entered or left, and no field was updated since the last check.' }),
          )
        : el('ul', { class: 'feed' }, ...items),
    ),
  );
}

/* ------------------------------------------------------------------ billing -- */

/** Listed plans, loaded when the billing screen opens. Drawn from state, never the DOM. */
let billingPlans: PlanInfo[] | null = null;

/* ------------------------------------------------------------ account screen -- */

/**
 * The account: who you are signed in as, and the password door (D34).
 *
 * Deliberately not routed through `run()`. That helper re-renders when it finishes, and a
 * re-render replaces the inputs — so a rejected password would take the other two fields
 * with it and the user would retype all three. Status is written into one element
 * instead, and the screen is re-rendered only on success, when the fields are meant to be
 * empty anyway.
 */
function accountScreen(): HTMLElement {
  const session = state.session;
  const user = session?.user;
  const has = user?.hasPassword === true;

  const current = el('input', { type: 'password', 'aria-label': 'Current password', autocomplete: 'current-password' });
  const next = el('input', { type: 'password', 'aria-label': 'New password', autocomplete: 'new-password', placeholder: `At least ${String(PASSWORD_MIN)} characters` });
  const confirm = el('input', { type: 'password', 'aria-label': 'Confirm new password', autocomplete: 'new-password' });
  const status = el('p', { class: 'sub' });

  const save = async (): Promise<void> => {
    if (next.value.length < PASSWORD_MIN) {
      next.focus();
      status.textContent = `A password needs at least ${String(PASSWORD_MIN)} characters.`;
      return;
    }
    if (next.value !== confirm.value) {
      // Checked here because the server cannot: it is sent one password, and a typo in a
      // field nobody can read is a password the user does not know they have.
      confirm.focus();
      status.textContent = 'Those two do not match.';
      return;
    }
    try {
      status.textContent = 'Saving…';
      const result = await api.setPassword(next.value, has ? current.value : undefined);
      state.session = await api.session();
      status.textContent = '';
      state.notice =
        result.otherSessionsSignedOut > 0
          ? `Password saved. ${plural(result.otherSessionsSignedOut, 'other session was', 'other sessions were')} signed out.`
          : 'Password saved.';
      render();
    } catch (err) {
      status.textContent = (err as ApiError).message;
    }
  };

  const remove = async (): Promise<void> => {
    if (current.value === '') {
      current.focus();
      status.textContent = 'Enter your current password to remove it.';
      return;
    }
    try {
      status.textContent = 'Removing…';
      await api.clearPassword(current.value);
      state.session = await api.session();
      status.textContent = '';
      state.notice = 'Password removed. Sign in by emailed link from now on.';
      render();
    } catch (err) {
      status.textContent = (err as ApiError).message;
    }
  };

  for (const field of [current, next, confirm]) {
    field.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') void save();
    });
  }

  return el(
    'div',
    { class: 'pad account' },
    el('h2', { style: 'margin:0 0 14px;font-size:19px', text: 'Your account' }),
    state.notice === null ? null : el('div', { class: 'banner banner-info', role: 'status' }, state.notice),
    el(
      'div',
      { class: 'panel' },
      el('div', { class: 'panel-head' }, el('h3', { text: 'Signed in as' })),
      el(
        'div',
        { class: 'panel-body' },
        el('p', { style: 'margin:0' }, el('strong', { text: user?.email ?? '' })),
        el('p', {
          class: 'note',
          text:
            `${user?.role === 'owner' ? 'Owner' : 'Member'} of ` +
            (session?.orgs?.find((o) => o.orgId === session.orgId)?.name ?? 'your workspace'),
        }),
      ),
    ),
    el(
      'div',
      { class: 'panel' },
      el('div', { class: 'panel-head' }, el('h3', { text: has ? 'Change your password' : 'Set a password' })),
      el(
        'div',
        { class: 'panel-body', style: 'max-width:420px' },
        el('p', {
          class: 'note',
          style: 'margin-top:0',
          text: has
            ? (user?.passwordSetAt === null || user?.passwordSetAt === undefined
                ? 'Changing it signs out every other session.'
                : `Last changed ${age(user.passwordSetAt)} ago. Changing it signs out every other session.`)
            : 'Optional. Without one you sign in by emailed link, which keeps working either way.',
        }),
        has ? el('label', { class: 'field-label', text: 'Current password' }) : null,
        has ? current : null,
        el('label', { class: 'field-label', text: has ? 'New password' : 'Password' }),
        next,
        el('label', { class: 'field-label', text: 'Confirm' }),
        confirm,
        el(
          'div',
          { style: 'display:flex;gap:8px;align-items:center;margin-top:14px' },
          el('button', { class: 'btn btn-primary', text: has ? 'Change password' : 'Set password', onclick: () => void save() }),
          has
            ? el('button', { class: 'btn btn-danger', text: 'Remove password', onclick: () => void remove() })
            : null,
        ),
        status,
      ),
    ),
  );
}

function billingScreen(): HTMLElement {
  const session = state.session;
  const isOwner = session?.user?.role === 'owner';
  const current = session?.plan?.code ?? null;
  const status = session?.status ?? null;
  const returned = state.route.name === 'billing' ? state.route.returned : null;
  const priceOf = (p: PlanInfo): string => `$${(p.priceCents / 100).toLocaleString('en-US')}/month`;
  /** Paying for it already. A trial is on a plan without having bought it. */
  const onThisPlan = (p: PlanInfo): boolean => p.code === current && status !== 'trialing' && status !== null;
  const limit = (n: number | null, unit: string): string => (n === null ? `Unlimited ${unit}` : `${n.toLocaleString('en-US')} ${unit}`);

  const send = (label: string, request: () => Promise<{ url: string }>): void =>
    void run(label, async () => {
      const { url } = await request();
      window.location.assign(url);
    });

  return el(
    'div',
    { class: 'ask' },
    el('h1', { text: 'Plan and billing' }),
    returned === 'success'
      ? el(
          'div',
          { class: 'banner banner-info' },
          el('strong', { text: 'Thanks. Your subscription is being set up.' }),
          el('div', {
            class: 'diff',
            text:
              current === null || status === 'trialing'
                ? 'Stripe confirms the payment to us in a moment. If the plan below has not changed yet, reload in a few seconds.'
                : 'Your plan is active.',
          }),
        )
      : returned === 'cancelled'
        ? el('div', { class: 'banner banner-warn' }, el('strong', { text: 'Checkout was cancelled. Nothing was charged.' }))
        : null,
    status === 'past_due'
      ? el(
          'div',
          { class: 'banner banner-warn' },
          el('strong', { text: 'Your last payment failed.' }),
          // D21: reading keeps working, spending stops. Say both.
          el('div', { class: 'diff', text: 'Your bases and exports keep working; enrichment and refresh are paused until the card is updated.' }),
        )
      : null,
    el('p', {
      class: 'note',
      text:
        current === null
          ? 'This organisation has no plan yet.'
          : `Current plan: ${session?.plan?.name ?? current}${status === null ? '' : ` (${status.replace('_', ' ')})`}.` +
            (isOwner ? '' : ' Only an owner can change the plan or billing details.'),
    }),
    state.error === null ? null : banner(state.error),
    billingPlans === null
      ? el('div', { class: 'skeleton', text: 'Loading plans…' })
      : el(
          'div',
          { style: 'display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:12px;margin-top:12px' },
          ...billingPlans.map((p) =>
            el(
              'div',
              { class: 'panel' },
              el('div', { class: 'panel-head' }, el('strong', { text: p.name }), el('span', { class: 'n cost', text: priceOf(p) })),
              el(
                'div',
                { class: 'panel-body' },
                el('div', { class: 'diff', text: limit(p.maxBases, 'bases') }),
                el('div', { class: 'diff', text: limit(p.maxRecords, 'records per base') }),
                el('div', { class: 'diff', text: limit(p.maxCustomColumns, 'custom columns') }),
                el('div', { class: 'diff', text: `$${p.monthlyCredits.toLocaleString('en-US')} of enrichment credit a month` }),
                el('div', { class: 'diff', text: p.refreshInterval === null ? 'No scheduled refresh' : `Refreshed every ${p.refreshInterval}` }),
                el('button', {
                  class: onThisPlan(p) ? 'btn' : 'btn btn-primary',
                  style: 'margin-top:10px',
                  disabled: onThisPlan(p) || !isOwner || state.busy !== null,
                  // Every workspace starts on a Growth trial, so "current plan, disabled"
                  // left a trial user with no way to buy the plan they were trialing,
                  // which is the only conversion that matters. The server always
                  // allowed it.
                  text: onThisPlan(p)
                    ? 'Current plan'
                    : p.code === current
                      ? 'Keep this plan'
                      : current === null
                        ? 'Choose'
                        : 'Switch',
                  onclick: () => send('Opening checkout', () => api.checkout(p.code)),
                }),
              ),
            ),
          ),
        ),
    el(
      'div',
      { style: 'margin-top:16px' },
      el('button', {
        class: 'btn',
        disabled: !isOwner || current === null || state.busy !== null,
        text: 'Manage billing: card, invoices, cancel',
        onclick: () => send('Opening billing portal', () => api.portal()),
      }),
      el('div', { class: 'note', style: 'margin-top:6px', text: 'Payments are handled by Stripe. We never see your card.' }),
    ),
  );
}

/* -------------------------------------------------------------------- shell -- */

/** Run an action with a busy label and a single place that turns errors into banners. */
async function run(label: string, action: () => Promise<void>): Promise<void> {
  state.busy = label;
  state.error = null;
  render();
  try {
    await action();
  } catch (err) {
    state.error = err instanceof ApiError ? err : new ApiError(0, 'unknown', String(err));
  } finally {
    state.busy = null;
    render();
  }
}

async function refreshPreview(): Promise<void> {
  if (state.draft === null) return;
  state.draft.preview = await api.preview(state.draft.plan, 5);
  render();
}

async function loadBases(): Promise<void> {
  state.bases = (await api.bases()).bases;
}

function go(hash: string): void {
  if (window.location.hash === hash) route();
  else window.location.hash = hash;
}

function render(): void {
  // Signed out: the sign-in screen and nothing else — no sidebar listing someone's
  // bases, no chrome implying an account.
  if (state.session !== null && !state.session.signedIn) {
    // There is no sidebar to put in the first grid track, so the shell stops being two
    // columns. Leaving it as one dropped the card into the 244px sidebar column, where
    // every line of it wrapped.
    root.className = 'app app-solo';
    const screen = state.route.name === 'signup' ? signUpScreen() : signInScreen();
    const body = state.error === null ? screen : frag(el('div', { class: 'gate gate-banner' }, banner(state.error)), screen);
    fill(root, el('main', { class: 'main' }, el('div', { class: 'screen' }, body)));
    return;
  }
  root.className = 'app';

  const main = el(
    'main',
    { class: 'main' },
    // Above the screen rather than inside it, so it is on the ask screen and the base
    // screen alike without either of them knowing about billing.
    trialBar(),
    state.route.name === 'ask'
      ? el('div', { class: 'screen' }, askScreen())
      : state.route.name === 'base'
        ? baseScreen()
        : state.route.name === 'billing'
          ? el('div', { class: 'screen' }, billingScreen())
          : state.route.name === 'account'
            ? el('div', { class: 'screen' }, accountScreen())
            : changesScreen(),
  );
  fill(root, sidebar(), main);
}

async function route(): Promise<void> {
  closePop();
  // A success message belongs to the screen that produced it.
  state.notice = null;
  const hash = window.location.hash || '#/ask';

  const signin = /^#\/signin\?token=(.+)$/.exec(hash);
  if (signin !== null) {
    await completeSignIn(decodeURIComponent(signin[1]!));
    return;
  }
  if (state.session !== null && !state.session.signedIn) {
    // Signed out: the only two screens are sign-in and sign-up; everything else is
    // sign-in with the destination remembered by the hash.
    if (hash === '#/signup') state.route = { name: 'signup' };
    else if (state.route.name === 'signup') state.route = { name: 'ask' };
    render();
    return;
  }
  if (hash === '#/signup') {
    // Signed in already: there is nothing to create.
    go('#/ask');
    return;
  }
  if (hash === '#/account') {
    state.route = { name: 'account' };
    stopWatching();
    render();
    return;
  }
  const base = /^#\/base\/(\d+)$/.exec(hash);
  const changes = /^#\/changes\/(\d+)$/.exec(hash);
  const billing = /^#\/billing(?:\?checkout=(success|cancelled))?$/.exec(hash);

  if (billing !== null) {
    state.route = { name: 'billing', returned: (billing[1] as 'success' | 'cancelled' | undefined) ?? null };
    render();
    await run('Loading plans', async () => {
      // After a checkout the webhook may land a moment after the browser does, so the
      // session is re-read here and the screen says so if the plan has not moved yet.
      const [plans, session] = await Promise.all([api.plans(), api.session()]);
      billingPlans = plans.plans;
      state.session = session;
    });
    return;
  }

  if (base !== null) {
    const id = Number(base[1]);
    // A poll belongs to the base that started it; every other destination stops it.
    if (state.route.name !== 'base' || state.route.id !== id) stopWatching();
    state.route = { name: 'base', id };
    view = null;
    render();
    await run('Loading', () => loadBase(id));
    return;
  }
  stopWatching();
  if (changes !== null) {
    const id = Number(changes[1]);
    state.route = { name: 'changes', id };
    changesView = null;
    render();
    await run('Loading changes', () => loadChanges(id));
    return;
  }
  state.route = { name: 'ask' };
  render();
}

window.addEventListener('hashchange', () => void route());

async function start(): Promise<void> {
  render();
  try {
    state.session = await api.session();
  } catch (err) {
    state.error = err instanceof ApiError ? err : new ApiError(0, 'unknown', String(err));
    state.session = { signedIn: false };
  }

  if (state.session.signedIn) {
    try {
      await Promise.all([loadRegistry(), loadBases()]);
    } catch (err) {
      state.error = err instanceof ApiError ? err : new ApiError(0, 'unknown', String(err));
    }
  }
  await route();
}

void start();
